/**
 * Arrange's third question, and the only one that may go unanswered: anything else.
 *
 * It is a wish in the reader's own words, read when Arrange is pressed (`lib/arrange-wish`).
 * Where the words can be read two ways the question is asked here, under them, with its
 * answers as buttons, and what of a wish could not be done is said here too: beside the words
 * it is about, not over the score.
 */

import { ASK_LIMIT, type AskQuestion } from "@app/shared-types";
import type { WishSaid } from "../lib/arrange-wish";
import ArrangeStep from "./ArrangeStep";

export interface ArrangeStepWishProps {
  readonly words: string;
  readonly question: AskQuestion | null;
  readonly said: WishSaid | null;
  /** Shut while the words are being read. */
  readonly reading: boolean;
  onWords(words: string): void;
  /** Answer the question with one of its choices, by the words it stands for. */
  onChoose(choice: string): void;
}

/** A sentence closed where its writer left it open, so the next one does not run on. */
function closed(sentence: string): string {
  return /[.!?…]$/.test(sentence.trim()) ? sentence.trim() : `${sentence.trim()}.`;
}

export default function ArrangeStepWish({
  words,
  question,
  said,
  reading,
  onWords,
  onChoose,
}: ArrangeStepWishProps): JSX.Element {
  return (
    <ArrangeStep number={3} title="Anything else?" aside="optional" htmlFor="arrange-wish">
      <textarea
        id="arrange-wish"
        rows={3}
        value={words}
        maxLength={ASK_LIMIT}
        readOnly={reading}
        onChange={(event) => onWords(event.target.value)}
        placeholder="Only bars 1 to 8 · add a counter-melody · as a waltz"
        className="w-full resize-y rounded-md border border-shell-seam bg-shell px-3 py-2.5 text-[14px] text-shell-lit outline-none placeholder:text-shell-dim focus:border-shell-live"
      />
      {question === null ? null : (
        <div
          role="group"
          aria-label="A question about your wish"
          className="flex flex-col gap-2.5 rounded-lg border border-shell-live bg-shell p-3.5"
        >
          <p className="text-[14px] text-shell-lit">{question.text}</p>
          <div className="flex flex-wrap gap-2">
            {question.choices.map((choice) => (
              <button
                key={choice.words}
                type="button"
                disabled={reading}
                onClick={() => onChoose(choice.words)}
                className="min-h-11 rounded-md border border-shell-seam bg-shell-key px-3.5 py-2 text-left text-[14px] text-shell-lit hover:bg-shell-hot"
              >
                {choice.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {said?.failed == null ? null : (
        <p role="alert" className="rounded-md bg-fault/20 px-3 py-2 text-[13px] text-shell-lit">
          Your words could not be read, so nothing was arranged: {closed(said.failed)}
        </p>
      )}
      {said?.unmet == null ? null : (
        <p role="status" className="text-[13px] text-shell-lit">
          Not done: {closed(said.unmet)}
        </p>
      )}
      {said?.note == null ? null : (
        <p role="status" className="text-[13px] text-shell-lit">
          {said.note}
        </p>
      )}
    </ArrangeStep>
  );
}
